package Structs

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import myName

@Serializable
class Agent(
    var user_password: String? = null,
    var user_name: String,
    val manager: Int = 0,
    val protected: Int = 0,
    val company_id: String = "",
    val company: String = "",
    val price_auth: Int = 0,
    val change_auth: Int = 0,
    val all_doc_auth: Int = 0,
    /** 0 none, 1 orders (collector sees only the order), 2 collector full catalog, 3 order plus available products, 4 menu, 5 force delivery photo/signature, 6 same as 1 plus a shortage-order checkbox on issue. */
    val orders_auth: Int = 0,
    val debts_auth: Int = 0,
    val suppliers_auth: Int = 0,
    val amounts_sum_auth: Int = 0,
    val internal_details: Int = 0,
    val returns: Int = 0,
    val is_agent: Int = 0, // 1 = line
    val is_collector: Int = 0, // 1 = collector
    val is_driver: Int = 0, // 1 = driver, >=2 = line driver (fetch all clients), 20 = shared pool (visible to every driver)
    val position: Int = 0, // 1 = priority
    val note_cancel: Int = 0,
    val can_create: Int = 1,
    val visit: Int = 0,
    val id: Int = -1,
    val print: Int = 0, // bit0=PDF, bit1=control print prices, bit2=always prices when controlling, bit3=auto-print, bit4(16)=WhatsApp/email with prices (print=19 = 3+16). print=-4: Bluetooth no prices, send PDF with prices. print=-100 or -40: NiHulKavMobile Bluetooth receipts go to a Zebra ZQ320 (ZPL) instead of the SEWOO LK-P41.
    val increase_id: Int = 0,
    val version: String = "2.1",
    val car: String = "",
    /** Shown name in UI / attendance; `user_name` may stay a generic line name */
    val display_name: String = "",
    /** user_info.phone — contact number shown on the users table */
    val phone: String? = null,
    val start_point: String? = null,
    val comment: String? = null,
    val active: Int = 1,
    var block_obligo: Int =0 ,
    var inventory: Int = 1,
    var english: Int = 0, // 1 = english
    val increase_tax_id:Int=0,
    val with_notes:Int=0,
    val end_point: String? = null, //"35.46375895995361,32.785039769284985"
    val show_dashboard: Int = 1, // 1 = can show dashboard, 0 = hide dashboard

    /**
     * Mobile JSON Authorization System — effective policy for NiHulKavMobile only.
     * Never present on `/login` or `/agents` (those stay legacy-only for the old
     * app); populated by a dedicated post-login fetch (`MobileDB.fetchMobileAuthPolicy`)
     * and cached here for the session/offline cache. Null until that fetch
     * succeeds at least once — NiHulKavMobile's MobilePolicyEvaluator falls back
     * to built-in role defaults whenever this is null, so the old app (which never
     * sets it) and a not-yet-synced mobile session both behave safely.
     */
    var mobileAuth: MobileAuthEnvelope? = null,

) : Named {
    val auth: Auth = Auth(
        price_auth = price_auth >= 1,
        change_auth = change_auth >= 1,
        all_doc_auth = all_doc_auth >= 1,
        orders_auth = orders_auth >= 1,
        debts_auth = debts_auth >= 1,
        suppliers_auth = suppliers_auth == 1,
        amounts_sum_auth = amounts_sum_auth >= 1,
        internal_details = internal_details == 1,
        returns = returns >= 1,
        note_cancel = note_cancel >= 1,
        visit = visit >= 1,
        create = can_create>= 1
    )

    override fun getActiveState(): Int {
        return 1
    }

    @myName("forcePricePrint")
    fun forcePricePrint(): Boolean? {
        if(print == -4)
            return false
        val controlPricePrint = (print / 2) % 2 == 1
        val forcePrice = (print / 4) % 2 == 1
        if (controlPricePrint)
            return forcePrice
        return null

    }

    /**
     * WhatsApp/email PDFs include prices even when print strips them.
     * Bit 4 (value 16): `print=19` is `3+16`.
     * `print=-4` is the legacy equivalent (Bluetooth without prices, send with prices).
     */
    @myName("forceSendPrice")
    fun forceSendPrice(): Boolean = print == -4 || (print >= 0 && (print / 16) % 2 == 1)

    /** Delivery notes (share/PDF): price_auth 4 always includes prices; otherwise follow print settings. */
    @myName("forceDeliveryNotePrice")
    fun forceDeliveryNotePrice(): Boolean? {
        if (price_auth == 4) return true
        return forcePricePrint()
    }

    override fun getConnectedName(): String {
        return user_name
    }

    override fun getConnectedId(): Int {
        return -1
    }


    override fun toString(): String {
        return user_name.toString()
    }

    fun isLineManager(): Boolean {
        return manager >= 1
    }

    fun isTopManager(): Boolean {
        return manager == 2
    }

    @myName("isAgent")
    fun isAgent(): Boolean {
        return is_agent == 1
    }
    fun toJson(): JsonObject {
        val map: MutableMap<String, JsonPrimitive> = mutableMapOf()

        map["id"] = JsonPrimitive(id.toString())
        map["user_name"] = JsonPrimitive(user_name)
        map["user_password"] = JsonPrimitive(user_password ?: "")
        map["comment"] = JsonPrimitive(comment ?: "")

        return JsonObject(map)
    }
    /**
     * Neither a line agent, a collector, nor a driver.
     * Creation sends this as user type `3` (`is_agent`/`is_driver`/`is_collector` all 0).
     */
    @myName("isWorker")
    fun isWorker(): Boolean = !isAgent() && !isDriver() && !isCollector()

    @myName("getDescription")
    fun getDescription(): String {
        return when {
            isAgent() -> "סוכן"
            isDriver() -> "נהג"
            isCollector() -> "מלקט"
            else -> "עובד"
        }
    }

    fun isProtected(): Boolean {
        return protected == 1
    }

    @myName("isDriver")
    fun isDriver(): Boolean {
        return is_driver >= 1
    }

    /**
     * Dummy / pool driver: clients attached to this agent in `clients_daily` are also
     * fetched and shown for every other driver (including `manager == 0`).
     * Not a line driver — login still only pulls this pool, not the whole catalog.
     */
    @myName("isSharedDriver")
    fun isSharedDriver(): Boolean {
        return is_driver == 20
    }

    @myName("isLineDriver")
    fun isLineDriver(): Boolean {
        return is_driver >= 2 && !isSharedDriver()
    }
    @myName("isLineCollector")
    fun isLineCollector(): Boolean {
        return is_collector == 2
    }

    @myName("isCollector")
    fun isCollector(): Boolean {
        return is_collector >= 1
    }

    /**
     * Prepare-amounts collector: edits order quantities then marks COLLECT_INITIATED
     * so a manager (`is_collector` 3) can create the delivery.
     * 11 = carton + picker name required on approve. 12 = same flow, skip those fields.
     */
    @myName("isPrepareCollector")
    fun isPrepareCollector(): Boolean = is_collector == 11 || is_collector == 12

    /** Opens order picking (`doc_type` 20) instead of creating a delivery/invoice. */
    fun isOrderPickingCollector(): Boolean = is_collector == 4 || isPrepareCollector()

    /** Picking approve screen shows and requires carton count + picker name. */
    fun requiresCollectKartonAndName(): Boolean = is_collector == 4 || is_collector == 11

    /** 1 = always show the product-line notes field. 3 = collector sees line notes but they are not copied onto created delivery/invoice. */
    fun showsProductLineNotes(): Boolean = with_notes == 1 || with_notes == 3

    /** Collector + with_notes=3: keep line notes on the order for picking, omit them from created documents. */
    fun omitProductLineNotesOnCreatedDocument(): Boolean = isCollector() && with_notes == 3

    /** 1 = can show dashboard, 0 = hide dashboard */
    fun canShowDashboard(): Boolean = show_dashboard == 1

    /** Numbering stream for tax invoices: dedicated tax stream when configured, else regular. */
    fun taxIncreaseId(): Int = if (increase_tax_id != 0) increase_tax_id else increase_id
}